import React, { useState, useEffect } from 'react';
import { useSalesData } from '../context/SalesDataContext';
import { useAuth } from '../context/AuthContext';
import {
    Users, Briefcase, FileText, Phone, Mail, MapPin,
    Building2, PieChart, Info, Building, Database, Fingerprint,
    ChevronDown, ChevronUp, ArrowRight, Check, LayoutDashboard, RefreshCw
} from 'lucide-react';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';
import { useCapabilities } from '../hooks/useCapabilities';
import CopyablePhone from './CopyablePhone';

/**
 * Pequeño botón que al hacer clic envía `value` al campo `field` del SalesForm.
 * Muestra un check verde momentáneo al pulsar para dar feedback visual.
 */
const FillButton = ({ field, value, label }) => {
    const { updateSectionData } = useSalesData();
    const [filled, setFilled] = useState(false);

    if (!value) return null;

    const handleFill = (e) => {
        e.stopPropagation();
        updateSectionData('SalesForm', { [field]: value }, 'zoho');
        setFilled(true);
        setTimeout(() => setFilled(false), 1500);
    };

    return (
        <button
            onClick={handleFill}
            title={`Copiar al formulario: ${label || field}`}
            className={`ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold transition-all duration-200 
                ${filled
                    ? 'bg-emerald-500/30 text-emerald-400 border border-emerald-500/40'
                    : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 hover:text-sky-300 border border-sky-500/40 hover:border-sky-500/60 shadow-lg shadow-sky-500/5'
                }`}
        >
            {filled ? <Check size={10} /> : <ArrowRight size={10} />}
            {filled ? 'OK' : '→'}
        </button>
    );
};

const Customer360Panel = ({ contactId, onClose }) => {
    const { sectionData } = useSalesData();
    const { authFetch } = useAuth();
    const { registerAppendix, setAppendixStatus } = useAppendix();
    const id = 'customer-360';

    const [activeTab, setActiveTab] = useState('accounts');
    const [isOpen, setIsOpen] = useState(true);
    const [data, setData] = useState({ accounts: [], contacts: [], contratosSuministro: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        registerAppendix(id, {
            title: 'Customer 360°',
            icon: LayoutDashboard
        });
    }, [registerAppendix]);

    useEffect(() => {
        setAppendixStatus(id, !!contactId);
    }, [contactId, setAppendixStatus]);

    const { hasCapability } = useCapabilities();

    // Manual search functions: the panel no longer triggers automatic searches.
    const phone = sectionData?.SalesForm?.phone;
    const email = sectionData?.SalesForm?.email;
    const nif = sectionData?.SalesForm?.nif;

    const performSearch = async (paramsObj) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams(paramsObj);
            const response = await authFetch(`/api/zoho/customer-info?${params.toString()}`);
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    setData({
                        accounts: result.data.accounts || [],
                        contacts: result.data.contacts || [],
                        contratosSuministro: result.data.contratosSuministro || []
                    });

                    // Optional: keep previous behavior of syncing discovered phone with backend
                    if (contactId) {
                        const firstContactPhone = result.data.contacts?.[0]?.phone;
                        const firstAccountPhone = result.data.accounts?.[0]?.phone;
                        const discoveredPhone = firstContactPhone || firstAccountPhone;

                        if (discoveredPhone) {
                            try {
                                const agentInfo = JSON.parse(localStorage.getItem('agentInfo') || '{}');
                                const agentEmail = agentInfo?.email || 'agente@espuma.com';

                                authFetch('/api/calls/start', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        contactId: contactId,
                                        agentEmail: agentEmail,
                                        callerPhone: discoveredPhone
                                    })
                                }).catch(() => {});
                            } catch (syncErr) {
                                console.warn("Error sincronizando teléfono con backend:", syncErr);
                            }
                        }
                    }
                }
            } else {
                setError('Error al obtener datos de Zoho CRM.');
            }
        } catch (err) {
            console.error("Error fetching Zoho data:", err);
            setError('Error de conexión con el backend.');
        } finally {
            setLoading(false);
        }
    };

    const renderEmptyState = (tabName) => (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-slate-500 bg-slate-800/20 rounded-lg border border-dashed border-slate-700">
            <Info className="mb-2 opacity-50" size={20} />
            <p className="text-sm">No se encontraron {tabName} coincidentes.</p>
        </div>
    );

    const FieldRow = ({ icon: Icon, iconClass, label, value, formField, fullWidth = false }) => (
        <div className={`flex items-center gap-2 text-sm text-slate-300 ${fullWidth ? 'col-span-2' : ''}`}>
            {Icon && <Icon size={13} className={iconClass || 'text-slate-500'} />}
            {label && <span className="text-slate-500 text-xs">{label}:</span>}
            {Icon === Phone && value ? (
                <CopyablePhone phone={value} className="flex-1" textClassName="text-xs" />
            ) : (
                <span className="flex-1 truncate text-xs" title={value}>{value || '-'}</span>
            )}
            <FillButton field={formField} value={value} label={label || formField} />
        </div>
    );

    if (!contactId) return null;

    return (
        <AppendixWrapper 
            id={id} 
            title="Customer 360°" 
            icon={LayoutDashboard} 
            className="w-[420px]"
            onClose={onClose}
            defaultPosition={{ x: 700, y: window.innerHeight - 550 }}
        >
            <div className="flex flex-col bg-slate-950">
                {/* Header (Sub-info) */}
                <div 
                    className="flex items-center justify-between p-3 bg-slate-900 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 transition-colors"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-100 text-sm">Vista 360° Zoho CRM</span>
                        {loading && <RefreshCw size={14} className="text-primary animate-spin" />}
                    </div>
                    {isOpen ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                </div>

                {isOpen && (
                    <div className="overflow-y-auto max-h-[500px] bg-slate-950">
                        {error && (
                            <div className="bg-red-500/10 text-red-400 p-2 px-4 text-xs border-b border-red-500/20">{error}</div>
                        )}
                
                        {/* Search controls: only visible to users with customer360 capability */}
                        {hasCapability('customer360') && (
                            <div className="p-4 flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-400">Buscar por:</span>
                                    <button
                                        onClick={() => performSearch({ phone })}
                                        disabled={!phone}
                                        className="px-3 py-1 bg-slate-800 hover:bg-slate-700 rounded text-sm text-slate-200 disabled:opacity-40"
                                    >Teléfono</button>
                                    <button
                                        onClick={() => performSearch({ email })}
                                        disabled={!email}
                                        className="px-3 py-1 bg-slate-800 hover:bg-slate-700 rounded text-sm text-slate-200 disabled:opacity-40"
                                    >Email</button>
                                    <button
                                        onClick={() => performSearch({ cif: nif })}
                                        disabled={!nif}
                                        className="px-3 py-1 bg-slate-800 hover:bg-slate-700 rounded text-sm text-slate-200 disabled:opacity-40"
                                    >CIF</button>
                                </div>
                            </div>
                        )}

                                {/* Tabs */}
                        <div className="flex border-b border-slate-800 bg-slate-900/30 px-1 pt-1">
                            {[
                                { key: 'accounts', label: 'Accounts', Icon: Building2, count: data.accounts?.length },
                                { key: 'contacts', label: 'Contactos', Icon: Users, count: data.contacts?.length },
                                { key: 'contratos', label: 'Contratos', Icon: FileText, count: data.contratosSuministro?.length }
                            ].map(({ key, label, Icon, count }) => (
                                <button
                                    key={key}
                                    onClick={(e) => { e.stopPropagation(); setActiveTab(key); }}
                                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-all border-b-2 mx-0.5 rounded-t-md
                                        ${activeTab === key ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}
                                >
                                    <Icon size={13} />
                                    {label}
                                    <span className="px-1 py-0.5 rounded-full bg-slate-800 text-[9px]">{count || 0}</span>
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        <div className="p-4 space-y-3">
                            {/* === ACCOUNTS === */}
                            {activeTab === 'accounts' && (
                                data.accounts.length === 0 ? renderEmptyState('Cuentas') :
                                    data.accounts.map(acc => (
                                        <div key={acc.id} className="p-3 border border-slate-700 bg-slate-800/40 rounded-xl">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-sm font-bold text-slate-100 flex items-center gap-1.5">
                                                    <Building className="text-indigo-400" size={14} />
                                                    {acc.account_name}
                                                </h3>
                                                <div className="flex items-center gap-1">
                                                    {acc.satisfaction_profile && (
                                                        <span className="text-[9px] px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 font-bold uppercase rounded border border-indigo-500/20">
                                                            {acc.satisfaction_profile}
                                                        </span>
                                                    )}
                                                    <FillButton field="full_name" value={acc.account_name} label="Nombre" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-2">
                                                <FieldRow icon={Fingerprint} label={acc.document_type || 'NIF'} value={acc.document_number} formField="nif" />
                                                <FieldRow icon={Phone} value={acc.phone} formField="phone" />
                                                <FieldRow icon={Mail} value={acc.email} formField="email" />
                                                {acc.full_address && (
                                                    <FieldRow
                                                        icon={MapPin}
                                                        value={[acc.full_address, acc.city, acc.province].filter(Boolean).join(', ')}
                                                        formField="address_full"
                                                        fullWidth
                                                    />
                                                )}
                                                {acc.zip_code && (
                                                    <FieldRow icon={Database} label="CP" value={acc.zip_code} formField="postal_code" />
                                                )}
                                            </div>
                                        </div>
                                    ))
                            )}

                            {/* === CONTACTS === */}
                            {activeTab === 'contacts' && (
                                data.contacts.length === 0 ? renderEmptyState('Contactos') :
                                    data.contacts.map(contact => (
                                        <div key={contact.id} className="p-3 border border-slate-700 bg-slate-800/40 rounded-xl">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-sm font-bold text-slate-100 flex items-center gap-1.5">
                                                    <Users className="text-emerald-400" size={14} />
                                                    {contact.full_name}
                                                </h3>
                                                <div className="flex items-center gap-1">
                                                    {contact.account && (
                                                        <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded border border-emerald-500/20 flex items-center gap-0.5">
                                                            <Building2 size={9} /> {contact.account}
                                                        </span>
                                                    )}
                                                    <FillButton field="full_name" value={contact.full_name} label="Nombre" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-2">
                                                <FieldRow icon={Fingerprint} label="NIF" value={contact.document_number} formField="nif" />
                                                <FieldRow icon={Phone} value={contact.phone} formField="phone" />
                                                <FieldRow icon={Mail} value={contact.email || contact.secondary_email} formField="email" />
                                                <div className="flex items-center gap-1 text-xs text-slate-400">
                                                    <Briefcase size={12} className="text-slate-500" />
                                                    <span>
                                                        <strong className="text-emerald-400">{contact.total_active_contracts || 0}</strong> act. / {contact.total_finished_contracts || 0} fin.
                                                    </span>
                                                </div>
                                                {contact.zip_code && (
                                                    <FieldRow icon={Database} label="CP" value={contact.zip_code} formField="postal_code" />
                                                )}
                                            </div>
                                        </div>
                                    ))
                            )}

                            {/* === CONTRATOS === */}
                            {activeTab === 'contratos' && (
                                data.contratosSuministro.length === 0 ? renderEmptyState('Contratos') :
                                    data.contratosSuministro.map(c => (
                                        <div key={c.id} className="p-3 border border-slate-700 bg-slate-800/40 rounded-xl">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-sm font-bold text-slate-100 flex items-center gap-1.5">
                                                    <FileText className="text-amber-400" size={14} />
                                                    {c.contract_number || 'Sin Ref'}
                                                </h3>
                                                <div className="flex items-center gap-1">
                                                    <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase rounded border
                                                    ${c.status === 'Activo' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20' :
                                                            c.status === 'Finalizado' ? 'bg-red-500/20 text-red-400 border-red-500/20' :
                                                                'bg-slate-500/20 text-slate-300 border-slate-500/20'}`}>
                                                        {c.status || '—'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-2">
                                                {c.cups && (
                                                    <div className="col-span-2 flex items-center gap-1 text-xs">
                                                        <span className="text-slate-500 text-[10px] font-bold uppercase">CUPS</span>
                                                        <span className="font-mono text-slate-300 text-[11px]">{c.cups}</span>
                                                        <FillButton field="cups" value={c.cups} label="CUPS" />
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-1 text-xs text-slate-300">
                                                    <PieChart size={12} className="text-amber-500" />
                                                    {c.tariff_alias || c.tariff_atr || '-'}
                                                </div>

                                                {c.supply_address && (
                                                    <div className="col-span-2 flex items-center gap-1 text-xs">
                                                        <MapPin size={12} className="text-slate-500 shrink-0" />
                                                        <span className="flex-1 text-slate-300 truncate">{c.supply_address}</span>
                                                        <FillButton field="address_full" value={c.supply_address} label="Dirección" />
                                                    </div>
                                                )}
                                                {c.supply_zip_code && (
                                                    <div className="flex items-center gap-1 text-xs col-span-2">
                                                        <span className="text-slate-500 text-[10px] font-bold uppercase shrink-0">Código Postal</span>
                                                        <span className="flex-1 text-slate-300 ml-1">{c.supply_zip_code}</span>
                                                        <FillButton field="postal_code" value={c.supply_zip_code} label="CP" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </AppendixWrapper>
    );
};

export default Customer360Panel;
